/// @ts-check
/**
 * 节点数据提取工具
 * 
 * 提供统一的节点数据提取接口，封装所有数据字段的查找逻辑
 * 减少重复代码，提高可维护性
 */

/**
 * @typedef {import('../types/graphData.types.js').GraphNode} GraphNode
 * @typedef {import('../types/graphData.types.js').StandardizedNode} StandardizedNode
 */

/**
 * 标准化节点数据
 * 
 * 将不同来源的节点数据统一转换为标准格式
 * 
 * @param {GraphNode} node - 原始节点
 * @returns {StandardizedNode} 标准化后的节点数据
 */
export const standardizeNode = (node) => {
  return {
    id: String(node.id),
    title: extractTitle(node),
    content: extractContent(node),
    author: extractAuthor(node),
    groupId: extractGroupId(node),
    timestamp: extractTimestamp(node),
    nodeType: extractNodeType(node),
    isPartnerResponse: isPartnerResponse(node)
  };
};

/**
 * 提取节点标题
 * 
 * 查找优先级：
 * 1. node.label 的第一行（ForumSticker 中 label 包含"标题\n\n作者\n时间"）
 * 2. node.data?.label 的第一行
 * 3. node.data.source.title
 * 4. node.title（可能包含完整内容，作为后备）
 * 
 * @param {GraphNode} node - 节点
 * @returns {string} 标题
 */
export const extractTitle = (node) => {
  // 优先从 label 提取第一行作为标题
  if (node.label) {
    return node.label.split('\n')[0].trim();
  }
  
  if (node.data?.label) {
    return node.data.label.split('\n')[0].trim();
  }
  
  // 后备选项
  return node.data?.source?.title || 
         node.title || 
         '';
};

/**
 * 提取节点内容
 * 
 * 查找优先级：
 * 1. node.content
 * 2. node.data.content
 * 3. node.data.source.content
 * 4. node.title（ForumSticker中将完整内容存在title，通常是HTML格式）
 * 
 * @param {GraphNode} node - 节点
 * @returns {string} 内容
 */
export const extractContent = (node) => {
  // 优先查找content字段
  const content = node.content || 
                  node.data?.content || 
                  node.data?.source?.content;
  
  // 如果content不存在，使用title（ForumSticker中title存储完整内容）
  if (!content && node.title) {
    return node.title;
  }
  
  return content || '';
};

/**
 * 提取节点作者
 * 
 * 查找优先级：
 * 1. node.data.source.author
 * 2. node.author
 * 3. node.data.author
 * 
 * @param {GraphNode} node - 节点
 * @returns {string} 作者
 */
export const extractAuthor = (node) => {
  return node.data?.source?.author || 
         node.author || 
         node.data?.author || 
         'Unknown';
};

/**
 * 提取节点所属群组 ID
 * 
 * 查找优先级：
 * 1. node.data.source.groupId
 * 2. node.groupId
 * 3. node.data.groupId
 * 4. 从 node.data.source.group 或 node.group 中解析
 * 
 * 注意：group 字段可能存储的是 tag/type 信息，而不是 groupId
 * 
 * @param {GraphNode} node - 节点
 * @returns {string} 群组 ID（如果找不到返回空字符串）
 */
export const extractGroupId = (node) => {
  // 优先从直接属性查找
  const directGroupId = node.data?.source?.groupId || 
                       node.groupId || 
                       node.data?.groupId;
  
  if (directGroupId !== undefined && directGroupId !== null) {
    return String(directGroupId);
  }
  
  // 尝试从 group 字段解析
  const groupData = node.data?.source?.group || node.group;
  
  if (typeof groupData === 'string') {
    try {
      const parsed = JSON.parse(groupData);
      if (parsed.groupId !== undefined) {
        return String(parsed.groupId);
      }
    } catch {
      // JSON 解析失败，继续
    }
  } else if (typeof groupData === 'object' && groupData !== null) {
    if (groupData.groupId !== undefined) {
      return String(groupData.groupId);
    }
  }
  
  return '';
};

/**
 * 提取节点时间戳
 * 
 * 查找优先级：
 * 1. node.createdAt (駝峰命名)
 * 2. node.created_at (下劃線命名)
 * 3. node.data.source.timestamp
 * 4. node.timestamp
 * 5. node.data.timestamp
 * 
 * @param {GraphNode} node - 节点
 * @returns {number} 时间戳（毫秒），如果找不到返回 0
 */
export const extractTimestamp = (node) => {
  const timestamp = node.createdAt ||
                   node.created_at ||
                   node.data?.source?.timestamp || 
                   node.timestamp || 
                   node.data?.timestamp ||
                   0;
  
  return typeof timestamp === 'string' ? new Date(timestamp).getTime() : Number(timestamp);
};

/**
 * 提取节点类型
 * 
 * 查找优先级：
 * 1. node.data.source.type
 * 2. node.type
 * 
 * @param {GraphNode} node - 节点
 * @returns {string} 节点类型
 */
export const extractNodeType = (node) => {
  const nodeType = node.data?.source?.type || node.type || 'unknown';
  return typeof nodeType === 'object' ? JSON.stringify(nodeType) : String(nodeType);
};

/**
 * 检查节点是否为伙伴回应
 * 
 * 检查位置：
 * 1. node.data.source.type 或 node.type（检查是否包含 'partner_response' 或 'aiGenerated'）
 * 2. node.data.source.group 或 node.group（同样检查）
 * 
 * @param {GraphNode} node - 节点
 * @returns {boolean} 是否为伙伴回应
 */
export const isPartnerResponse = (node) => {
  // 检查 type 字段
  const nodeType = node.data?.source?.type || node.type;
  
  if (typeof nodeType === 'string') {
    if (nodeType.includes('partner_response') || nodeType.includes('aiGenerated')) {
      return true;
    }
  }
  
  // 检查 group 字段（某些模式下，类型信息存储在这里）
  const groupData = node.data?.source?.group || node.group;
  
  if (typeof groupData === 'string') {
    if (groupData.includes('partner_response') || groupData.includes('aiGenerated')) {
      return true;
    }
  } else if (typeof groupData === 'object' && groupData !== null) {
    if (groupData.type === 'partner_response' || groupData.aiGenerated === true) {
      return true;
    }
  }
  
  return false;
};

/**
 * 提取节点的完整文本（标题 + 内容）
 * 
 * @param {GraphNode} node - 节点
 * @returns {string} 完整文本
 */
export const extractFullText = (node) => {
  const title = extractTitle(node);
  const content = extractContent(node);
  
  if (title && content) {
    return `${title}\n\n${content}`;
  }
  
  return title || content || '';
};

/**
 * 检查节点是否有效（有标题或内容）
 * 
 * @param {GraphNode} node - 节点
 * @returns {boolean} 是否有效
 */
export const isValidNode = (node) => {
  const title = extractTitle(node);
  const content = extractContent(node);
  
  return (title && title.trim().length > 0) || (content && content.trim().length > 0);
};

